import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { OFFERS_NAMESPACE } from '@/i18n'
import { nextStep, OfferWizardStepId } from '@/pages/Offers/offer-wizard-steps'
import { type OfferCreateBody, offerService } from '@/services/offer.service'
import { apiMessage } from './api-message'

/**
 * The wizard's create handoff: Step 1 creates the draft (TO-125: `POST /offers`
 * with the recipient alone) and opens Step 2 for it; on a refusal it stays and
 * exposes the API message (TO-466 BR-12).
 */
export function useCreateOffer(): {
	submit: (body: OfferCreateBody) => void
	isSubmitting: boolean
	error: string | null
} {
	const { t } = useTranslation(OFFERS_NAMESPACE)
	const navigate = useNavigate()
	const mutation = useMutation({
		mutationFn: (body: OfferCreateBody) => offerService.create(body),
		onSuccess: (offer) => void navigate(nextStep(OfferWizardStepId.RECIPIENT)?.path(offer.offerId) ?? '/offers'),
	})
	const message = mutation.error ? (apiMessage(mutation.error) ?? t('wizard.createError')) : null
	return { submit: (body) => mutation.mutate(body), isSubmitting: mutation.isPending, error: message }
}
